import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { tt } from '@/internationalization/i18n'
import { toast, useSessionStore } from '@/stores'
import {
  createRole,
  deleteRole,
  fetchMyAuthorization,
  fetchPermissionCatalogue,
  fetchRoles,
  updateRole,
} from './api'

export function useRoles() {
  return useQuery({ queryKey: qk.authorization.roles, queryFn: fetchRoles })
}

/**
 * The permission catalogue. Requires `role:manage`, so it is gated by `enabled`
 * rather than left to 403 — a query that always fails retries, logs and shows
 * an error boundary for a screen the person is not meant to be on.
 */
export function usePermissionCatalogue(enabled: boolean) {
  return useQuery({
    queryKey: qk.authorization.catalogue,
    queryFn: fetchPermissionCatalogue,
    enabled,
    staleTime: Infinity, // Changes only when the product ships a migration.
  })
}

/**
 * Every role refusal is something a person can act on — "only somebody who
 * holds the Owner role may change it", "you cannot grant a permission you do
 * not hold" — so the server's sentence is shown rather than collapsed into
 * "could not save". That wording is the only place the protection rule is
 * explained at the moment somebody hits it.
 */
function explain(error: unknown, fallback: string) {
  if (error instanceof ApiError && error.status < 500) {
    return toast.error(fallback, error.message)
  }
  return toast.error(fallback, tt('common.somethingWentWrong'))
}

function useRoleMutation<TArgs, TResult>(
  fn: (args: TArgs) => Promise<TResult>,
  fallback: string,
  onDone?: (result: TResult) => void,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: qk.authorization.all })
      // The member list shows a role per person, so a rename lands there too.
      queryClient.invalidateQueries({ queryKey: qk.settings.members })
      // AND THE EDITOR'S OWN PERMISSIONS MAY HAVE JUST CHANGED. Editing the
      // role you hold takes effect on your next request, so the sidebar and
      // every gated button have to be re-read now rather than at next reload
      // — otherwise you keep a menu of things the API will refuse.
      try {
        const mine = await fetchMyAuthorization()
        useSessionStore.getState().setPermissions(mine.permissions)
      } catch {
        /* the API stays authoritative; the menu catches up on reload */
      }
      onDone?.(result)
    },
    onError: (error) => explain(error, fallback),
  })
}

export const useCreateRole = () =>
  useRoleMutation(createRole, tt('roles.toast.createFail'), (role) =>
    toast.success(tt('roles.toast.created'), tt('roles.toast.createdBody', { name: role.name })),
  )

export const useUpdateRole = () =>
  useRoleMutation(
    ({ id, patch }: { id: string; patch: Parameters<typeof updateRole>[1] }) =>
      updateRole(id, patch),
    tt('roles.toast.saveFail'),
    (role) =>
      toast.success(
        tt('roles.toast.saved'),
        tt('roles.toast.savedBody', {
          count: role.memberCount,
          verb: role.memberCount === 1 ? tt('roles.toast.savedVerbOne') : tt('roles.toast.savedVerbMany'),
          name: role.name,
        }),
      ),
  )

export const useDeleteRole = () =>
  useRoleMutation(
    (id: string) => deleteRole(id),
    tt('roles.toast.deleteFail'),
    () => toast.info(tt('roles.toast.deleted')),
  )
